import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import { useCart } from '../context/CartContext';
import { Check } from 'lucide-react';
import { fetchBundleProduct, toShopifyGid } from '../lib/shopify-storefront';

const FALLBACK_BUNDLE = {
  id: 'bundle',
  shopifyProductId: '8246763716793',
  shopifyVariantId: '45450776150201',
  shopifySellingPlanId: '3691937977',
  title: 'Complete Wellness Bundle',
  subtitle: 'All 3 Premium Products',
  description: 'Get Rush, Synaptiq, and Vitra together for maximum wellness benefits.',
  oneTimePrice: 133.43,
  subscriptionPrice: 120.09,
  image: '/products/bundle.png',
  includes: [
    { name: 'RUSH', subtitle: 'Focus & Energy', price: 48.99, image: '/products/rush-1.png' },
    { name: 'SYNAPTIQ', subtitle: 'Neuro Performance', price: 48.99, image: '/products/snaptiq-1.png' },
    { name: 'VITRA', subtitle: 'Brain Body Balance', price: 58.99, image: '/products/vitra-1.png' },
  ],
};

export function BundlePage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount, addToCart } = useCart();
  const [purchaseType, setPurchaseType] = useState('subscription');
  const [loading, setLoading] = useState(true);
  const [bundleData, setBundleData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadBundleData() {
      setLoading(true);
      const shopifyBundle = await fetchBundleProduct();
      setBundleData(shopifyBundle ? { ...FALLBACK_BUNDLE, ...shopifyBundle } : FALLBACK_BUNDLE);
      setLoading(false);
    }
    loadBundleData();
  }, []);

  const oneTimePrice = bundleData?.oneTimePrice || FALLBACK_BUNDLE.oneTimePrice;
  const subscriptionPrice = bundleData?.subscriptionPrice || FALLBACK_BUNDLE.subscriptionPrice;
  const displayPrice = purchaseType === 'subscription' ? subscriptionPrice : oneTimePrice;
  const individualTotal = FALLBACK_BUNDLE.includes.reduce((sum, p) => sum + p.price, 0);
  const savings = individualTotal - displayPrice;
  const bundleDiscount = individualTotal - oneTimePrice;
  const subscriptionDiscount = oneTimePrice - subscriptionPrice;

  const handleAddToCart = () => {
    if (!bundleData) return;
    const variantGid = bundleData.variantGid || toShopifyGid(bundleData.shopifyVariantId, 'ProductVariant');
    const sellingPlanGid = purchaseType === 'subscription'
      ? (bundleData.sellingPlanGid || toShopifyGid(bundleData.shopifySellingPlanId, 'SellingPlan'))
      : null;
    addToCart({
      ...bundleData,
      price: displayPrice,
      variantId: variantGid,
      isSubscription: purchaseType === 'subscription',
      sellingPlanId: sellingPlanGid,
      subtitle: 'All 3 Premium Products',
      image: bundleData.image || '/products/bundle.png',
      name: bundleData.title,
    }, 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAF9F6' }}>
        <Navigation onCartOpen={() => setIsCartOpen(true)} cartItemsCount={cartItemsCount} />
        <motion.img
          src="/logo/berleto-logo-dark.png"
          alt="Berleto"
          className="w-12 h-12 object-contain"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, ease: 'linear', repeat: Infinity }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation onCartOpen={() => setIsCartOpen(true)} cartItemsCount={cartItemsCount} />

      {/* Hero */}
      <section className="pt-32 pb-20" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
                Bundle
              </p>
              <h1 className="text-5xl md:text-6xl font-light leading-tight mb-6" style={{ color: '#1A2A1A' }}>
                All three.<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>One decision.</span>
              </h1>
              <p className="text-sm leading-relaxed mb-10" style={{ color: 'rgba(26,42,26,0.55)', maxWidth: '44ch' }}>
                RUSH, SYNAPTIQ, and VITRA — the complete Berleto stack. Everything your body and mind need, bundled together and priced to make the choice easy.
              </p>

              {/* Savings stat */}
              <div className="flex items-baseline gap-3">
                <span className="text-5xl font-light" style={{ color: '#1A2A1A' }}>
                  {purchaseType === 'subscription' ? '25%' : '15%'}
                </span>
                <span className="text-sm" style={{ color: 'rgba(26,42,26,0.45)' }}>
                  {purchaseType === 'subscription'
                    ? 'off when you bundle all three and subscribe — 15% bundle savings plus 10% subscription'
                    : 'off when you bundle all three vs. buying individually'}
                </span>
              </div>
            </motion.div>

            {/* 3 product bottles */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-end justify-center gap-4"
            >
              {[
                { src: '/products/rush-1.png', name: 'RUSH', h: '200px' },
                { src: '/products/vitra-1.png', name: 'VITRA', h: '260px' },
                { src: '/products/snaptiq-1.png', name: 'SYNAPTIQ', h: '200px' },
              ].map((p, i) => (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-col items-center gap-3"
                >
                  <img
                    src={p.src}
                    alt={p.name}
                    className="object-contain drop-shadow-xl"
                    style={{ height: p.h }}
                  />
                  <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: 'rgba(26,42,26,0.4)' }}>
                    {p.name}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="border-y" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-3 divide-x" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
            {FALLBACK_BUNDLE.includes.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="px-8 py-8"
              >
                <p className="text-xs font-medium tracking-[0.25em] uppercase mb-2" style={{ color: '#B8704D' }}>
                  {p.name}
                </p>
                <p className="text-sm mb-3" style={{ color: 'rgba(26,42,26,0.5)' }}>{p.subtitle}</p>
                <p className="text-base font-light line-through" style={{ color: 'rgba(26,42,26,0.3)' }}>
                  ${p.price.toFixed(2)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Purchase */}
      <section className="py-24" style={{ background: '#E8DCC4' }}>
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">

            {/* Price breakdown */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-8" style={{ color: '#B8704D' }}>
                Price breakdown
              </p>

              <div className="space-y-0">
                {FALLBACK_BUNDLE.includes.map((p, i) => (
                  <div
                    key={p.name}
                    className="flex justify-between items-center py-4 border-b"
                    style={{ borderColor: 'rgba(26,42,26,0.1)' }}
                  >
                    <span className="text-sm" style={{ color: '#1A2A1A' }}>{p.name}</span>
                    <span className="text-sm" style={{ color: 'rgba(26,42,26,0.5)' }}>${p.price.toFixed(2)}</span>
                  </div>
                ))}

                <div
                  className="flex justify-between items-center py-4 border-b"
                  style={{ borderColor: 'rgba(26,42,26,0.1)' }}
                >
                  <span className="text-sm" style={{ color: 'rgba(26,42,26,0.55)' }}>Subtotal</span>
                  <span className="text-sm line-through" style={{ color: 'rgba(26,42,26,0.4)' }}>${individualTotal.toFixed(2)}</span>
                </div>

                <div
                  className="flex justify-between items-center py-4 border-b"
                  style={{ borderColor: 'rgba(26,42,26,0.1)' }}
                >
                  <span className="text-sm" style={{ color: 'rgba(26,42,26,0.55)' }}>Bundle discount (15%)</span>
                  <span className="text-sm font-medium" style={{ color: '#22c55e' }}>−${bundleDiscount.toFixed(2)}</span>
                </div>

                {purchaseType === 'subscription' && (
                  <div
                    className="flex justify-between items-center py-4 border-b"
                    style={{ borderColor: 'rgba(26,42,26,0.1)' }}
                  >
                    <span className="text-sm" style={{ color: 'rgba(26,42,26,0.55)' }}>Subscription discount (10%)</span>
                    <span className="text-sm font-medium" style={{ color: '#22c55e' }}>−${subscriptionDiscount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between items-center pt-6">
                  <span className="text-base font-medium" style={{ color: '#1A2A1A' }}>Total</span>
                  <span className="text-4xl font-light" style={{ color: '#1A2A1A' }}>${displayPrice.toFixed(2)}</span>
                </div>
              </div>
            </motion.div>

            {/* Purchase options */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-8" style={{ color: '#B8704D' }}>
                Purchase type
              </p>

              {/* Toggle */}
              <div className="grid grid-cols-2 gap-0 border mb-8" style={{ borderColor: 'rgba(26,42,26,0.15)' }}>
                {[
                  { key: 'one-time', label: 'One-Time', sublabel: 'Save 15%' },
                  { key: 'subscription', label: 'Subscribe', sublabel: 'Save 25%' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setPurchaseType(opt.key)}
                    className="py-5 px-6 text-left transition-colors border-r last:border-r-0"
                    style={{
                      borderColor: 'rgba(26,42,26,0.15)',
                      background: purchaseType === opt.key ? '#1A2A1A' : 'transparent',
                    }}
                  >
                    <p className="text-sm font-medium mb-1" style={{ color: purchaseType === opt.key ? '#FAF9F6' : '#1A2A1A' }}>
                      {opt.label}
                    </p>
                    <p className="text-xs" style={{ color: purchaseType === opt.key ? '#B8704D' : 'rgba(26,42,26,0.4)' }}>
                      {opt.sublabel}
                    </p>
                  </button>
                ))}
              </div>

              {/* Benefits */}
              <div className="space-y-3 mb-8">
                {[
                  'All 3 premium products included',
                  'Free USA shipping',
                  '30-day satisfaction guarantee',
                  ...(purchaseType === 'subscription' ? ['Cancel anytime, no commitment', 'Auto-delivered every 30 days'] : []),
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm" style={{ color: 'rgba(26,42,26,0.7)' }}>
                    <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#22c55e' }} />
                    {b}
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={handleAddToCart}
                disabled={!bundleData}
                className="w-full py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-40"
                style={{ background: '#1A2A1A', color: '#FAF9F6' }}
                onMouseEnter={e => e.currentTarget.style.background = '#243424'}
                onMouseLeave={e => e.currentTarget.style.background = '#1A2A1A'}
              >
                Add Bundle to Cart — ${displayPrice.toFixed(2)}
              </button>

              <p className="mt-4 text-xs text-center" style={{ color: 'rgba(26,42,26,0.4)' }}>
                You save ${savings.toFixed(2)} compared to individual purchases
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why bundle */}
      <section className="py-24" style={{ background: '#1A2A1A' }} data-bubble-theme="light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              The full stack
            </p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: '#FAF9F6' }}>
              Why the bundle works.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 border-t" style={{ borderColor: 'rgba(139,154,139,0.15)' }}>
            {[
              {
                name: 'RUSH',
                role: 'Morning',
                description: 'Cordyceps, Reishi, Lion\'s Mane and Shiitake for sustained physical energy and immune resilience.',
              },
              {
                name: 'SYNAPTIQ',
                role: 'Focus',
                description: 'A nootropic blend of adaptogens, choline, and biotin for cognitive clarity and mental sharpness.',
              },
              {
                name: 'VITRA',
                role: 'Foundation',
                description: 'Seven organic mushrooms plus a complete vitamin and mineral complex for whole-body longevity support.',
              },
            ].map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`px-8 py-10 border-b md:border-b-0 ${i < 2 ? 'md:border-r' : ''}`}
                style={{ borderColor: 'rgba(139,154,139,0.15)' }}
              >
                <p className="text-xs font-medium tracking-[0.25em] uppercase mb-1" style={{ color: '#B8704D' }}>
                  {p.name}
                </p>
                <p className="text-xs tracking-widest uppercase mb-5" style={{ color: 'rgba(139,154,139,0.5)' }}>
                  {p.role}
                </p>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(139,154,139,0.65)' }}>
                  {p.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Individual products CTA */}
      <section className="py-16" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-sm" style={{ color: 'rgba(26,42,26,0.5)' }}>
              Prefer to start with just one?
            </p>
            <div className="flex gap-4">
              {['rush', 'synaptiq', 'vitra'].map((id) => (
                <button
                  key={id}
                  onClick={() => navigate(`/products/${id}`)}
                  className="px-6 py-2.5 text-xs tracking-[0.2em] uppercase transition-colors border"
                  style={{ borderColor: 'rgba(26,42,26,0.15)', color: 'rgba(26,42,26,0.6)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#1A2A1A'; e.currentTarget.style.color = '#FAF9F6'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(26,42,26,0.6)'; }}
                >
                  {id}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
